import { computed, onMounted, readonly, ref } from 'vue';
import { useRoute } from 'vue-router/composables';
import { useLanguageStore, useTitleDetailsStore } from '@/helpers/composables/useStores';
import { useUserAgent } from '@/helpers/composables/useUserAgent';

import { toValue, type MaybeRefOrGetter } from '@vueuse/core';

import type { ClickoutUrl } from '@/helpers/clickout-helper';
import type { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';

const sessionStorage = process.client && window != undefined ? window.sessionStorage : null;

// prettier-ignore
const ArbitrationCountries = [
	'AR', 'AT', 'AU', 'BE', 'BR', 'CA', 'CH', 'CL', 'CO', 'CZ', 'DE', 'DK', 'ES', 'FI', 'FR', 'GB', 'IE', 'IL', 'IN', 'IT', 'MX', 'NL', 'NO', 'NZ', 'PH', 'PL', 'PT', 'SA', 'SE', 'US', 'ZA',
] as const;

const EMPTY = '$' as const;

type ArbitrageParam = 'utm_campaign' | 'utm_content' | 'utm_medium';
const utmParams: ArbitrageParam[] = ['utm_campaign', 'utm_content', 'utm_medium'];

interface ArbitrationValues {
	titleid: string;
	device: 'm' | 'd';
	utm_medium: string;
	utm_content: string;
	utm_campaign: string;
}

const _isArbitrating = ref(false);
export const isArbitrating = readonly(_isArbitrating);

const ArbitrageProviders = [
	350, // Apple TV+
	2, // iTunes
	15, // Hulu
];

export function useArbitrage() {
	const route = useRoute();
	const { country } = useLanguageStore();
	const { titleContext } = useTitleDetailsStore();

	onMounted(() => {
		if (!ArbitrationCountries.includes(country.value)) return;
		if (_isArbitrating.value || process.server) return;

		type UTMQueryParams = [string, string | null][];
		const utmValues = utmParams
			.map(param => [param, route.query[param] as string | null])
			.filter(([, value]) => value != null) as UTMQueryParams;
		if (utmValues.length === 0) return;

		utmValues.forEach(([key, value]) => sessionStorage?.setItem(key, value!));

		_isArbitrating.value = true;
	});

	const clearArbitration = () => {
		utmParams.forEach(param => sessionStorage?.removeItem(param));

		_isArbitrating.value = false;
	};

	const { deviceInfo, deviceType } = useUserAgent();

	const arbitration = computed<ArbitrationValues | null>(() => {
		if (!isArbitrating.value) return null;

		const utm = utmParams.map(param => sessionStorage?.getItem(param) ?? undefined);
		if (utm.every(param => param == null)) return null;

		const [utm_campaign = EMPTY, utm_content = EMPTY, utm_medium = EMPTY] = utm;
		const titleid: string = titleContext.value?.jwEntityId ?? EMPTY;
		const device = deviceType.value === 'mobile' ? 'm' : 'd';

		return { utm_medium, titleid, device, utm_content, utm_campaign };
	});

	/** Sets the 'uct_ct' query on the Offer URL if needed. */
	function withArbitrageTracking(url: ClickoutUrl, offer: MaybeRefOrGetter<TitleOfferFragment | null>) {
		const packageId = toValue(offer)?.package.packageId ?? 0;
		const handler = getArbitrationHandler(packageId);

		if (ArbitrageProviders.includes(packageId) && arbitration.value && handler) {
			return handler.plugin(url, arbitration.value);
		}

		return url;
	}

	function trackTagEvent(offer: MaybeRefOrGetter<TitleOfferFragment | null>) {
		if (!window || !window.dataLayer) return;
		if (!isArbitrating.value) return;

		const packageId = toValue(offer)?.package.packageId ?? 0;
		const handler = getArbitrationHandler(packageId);

		if (ArbitrageProviders.includes(packageId)) {
			window.dataLayer.push({
				event: 'interaction',
				// event category
				target: 'clickout',
				// event label
				'target-properties': handler?.clearName ?? '',
				device: deviceInfo.value,
			});
		}
	}

	return {
		isArbitrating,
		clearArbitration,
		withArbitrageTracking,
		trackTagEvent,
	};
}

function getArbitrationHandler(packageId: number): ArbitrationHandler | null {
	switch (packageId) {
		case 2:
		case 350:
			return appleTvPlus;
		case 15:
			return hulu;
		default:
			return null;
	}
}

interface ArbitrationHandler {
	clearName: string;
	plugin: (url: ClickoutUrl, values: ArbitrationValues) => ClickoutUrl;
}

const appleTvPlus: ArbitrationHandler = {
	clearName: 'Apple TV Plus',
	plugin(url: ClickoutUrl, { utm_medium, titleid, device, utm_content, utm_campaign }: ArbitrationValues) {
		/* ct=<utm_medium>-<title id>-<device>-<utm_content>-<utm_campaign> */
		return url.set('uct_ct', [utm_medium, titleid, device, utm_content, utm_campaign].join('-'));
	},
};

const hulu: ArbitrationHandler = {
	clearName: 'Hulu',
	plugin(url: ClickoutUrl, { utm_medium, titleid, utm_content }: ArbitrationValues) {
		const offerLink = url.get('r');
		if (!offerLink) return url;

		const offerUrl = new URL(offerLink);
		offerUrl.searchParams.set('sub2', utm_medium);
		offerUrl.searchParams.set('sub3', utm_content);
		offerUrl.searchParams.set('sub4', titleid);

		return url.set('r', offerUrl.toString());
	},
};
